import {
  getAppliedCouponDiscountRupee,
  getAppliedLoyaltyDiscountRupee,
  isLoyaltyOtpComplete,
  normalizeLoyaltyOtp,
} from './commerce';
import { v2Request } from './client';
import { Capacitor } from '@capacitor/core';

/**
 * Build POST /api/orders body for COD (omit transactionRef — server sets cash_on_delivery).
 * @param {{ mobile: string, loc: object, checkoutState: { deliveryDate: string, timeSlot: object, paymentType?: string, instructions?: string }, cart: { products: array, totalAmount: number, coupon?: object|null, instructions?: string }, shippingCost: number }} p
 */
export function buildPilotCodOrderPayload({ mobile, loc, checkoutState, cart, shippingCost }) {
  const addr = loc?.customerAddress;
  const lat = Number(loc?.lat ?? addr?.latLong?.lat);
  const lng = Number(loc?.lng ?? addr?.latLong?.lng);

  const deliveryAddress =
    addr && typeof addr === 'object'
      ? {
          ...addr,
          latLong:
            Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : addr.latLong ?? null,
        }
      : null;

  const slot = checkoutState?.timeSlot;
  const slotId = slot?.id != null ? String(slot.id) : null;

  const subtotal = Math.max(0, Number(cart?.totalAmount) || 0);
  const ship = Math.max(0, Number(shippingCost) || 0);
  const loyaltyPoints = getAppliedLoyaltyDiscountRupee(cart);
  const loyaltyOtp = normalizeLoyaltyOtp(cart?.loyalty?.otp);
  const loyaltyApplied = loyaltyPoints > 0 && isLoyaltyOtpComplete(loyaltyOtp);
  const couponDiscount = loyaltyApplied ? 0 : getAppliedCouponDiscountRupee(cart);
  const appliedCoupon =
    !loyaltyApplied && couponDiscount > 0 && cart?.coupon?.couponCode
      ? String(cart.coupon.couponCode).trim()
      : null;
  const couponDiscountType =
    cart?.coupon?.discountType === 'free_sku' ? 'free_sku' : 'discount_coupon';
  const discount = loyaltyApplied ? loyaltyPoints : couponDiscount;
  const total = Math.max(0, subtotal + ship - discount);

  const branchId = loc?.store?.branchId ?? loc?.store?.branch_id;

  const instructionsTrimmed =
    (cart?.instructions ?? checkoutState?.instructions ?? '').trim() || null;

  return {
    userId: String(mobile).trim(),
    addressId: addr?.addressId != null ? String(addr.addressId) : undefined,
    deliveryAddress,
    storeId: loc?.storeId != null ? String(loc.storeId) : undefined,
    storeDetails: loc?.store && typeof loc.store === 'object' ? loc.store : undefined,
    branch_id: branchId != null && branchId !== '' ? String(branchId) : undefined,
    timeSlotId: slotId,
    timeSlotDetails: slot && typeof slot === 'object' ? slot : undefined,
    pranaSlotId: null,
    deliveryDate: checkoutState?.deliveryDate,
    deliveryDateTimestamp: Date.now(),
    products: Array.isArray(cart?.products) ? cart.products : [],
    subtotal,
    shippingCost: ship,
    discount,
    discountType: loyaltyApplied ? 'loyalty' : appliedCoupon ? couponDiscountType : null,
    appliedCoupon: loyaltyApplied ? 'loyalty' : appliedCoupon,
    ...(loyaltyApplied
      ? { loyaltyPointsUsed: loyaltyPoints, loyaltyOtp }
      : {}),
    instructions: instructionsTrimmed,
    total,
    platform: Capacitor.getPlatform().toUpperCase(),
    deliveryType: 'Home Delivery',
    orderType: 'customer_order',
    createdBy: 'customer',
    status: 'pending',
  };
}

/** Create order via Pilot POST /api/orders */
export async function createPilotOrder(body) {
  return v2Request('/api/orders', { method: 'POST', body });
}

/**
 * Fetch all orders for a given user (newest first) via Pilot GET /api/orders/user/:userId.
 * @param {string} userId — typically the customer's mobile number.
 * @returns {Promise<Array>} normalized list of orders (server already sorts by timeStamp desc).
 */
export async function fetchUserOrders(userId) {
  if (!userId) return [];
  const data = await v2Request(`/api/orders/user/${encodeURIComponent(String(userId))}`);
  return Array.isArray(data?.orders) ? data.orders : [];
}

/**
 * Fetch a single order by Pilot order id (document id or `orderId` field).
 * @param {string} orderId
 * @returns {Promise<object>} order payload from `GET /api/orders/:orderId`
 */
export async function fetchOrderById(orderId) {
  const id = String(orderId || '').trim();
  if (!id) {
    throw new Error('Missing order id');
  }
  const data = await v2Request(`/api/orders/${encodeURIComponent(id)}`);
  if (!data?.success || !data?.order) {
    throw new Error(data?.message || 'Order not found');
  }
  return data.order;
}
